import { u2000SumHonorariumDayTotals } from "./U2000TimesheetHonorariumDayTotals";
import { u2000SumWeekHoursOnTimesheetBankRow } from "./U2000TimesheetBankWeekTotals";

const WEEK_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

const ABSENCE_BANK_IDS = new Set(["vacation", "holidayLeave", "sickLeave", "authorizedLeave"]);

export type U2000TimesheetBankAggregateRow = Record<string, unknown> & {
  bankId?: string;
};

export type U2000TimesheetBankAggregates = {
  absenceHours: number;
  specialWithdraw: number;
  specialDeposit: number;
  specialNet: number;
};

export function u2000AggregateTimesheetBankHours(
  list: readonly U2000TimesheetBankAggregateRow[] | undefined | null,
): U2000TimesheetBankAggregates {
  let absenceHours = 0;
  let specialWithdraw = 0;
  let specialDeposit = 0;

  for (const row of list ?? []) {
    const bankId = String(row.bankId ?? "").trim();
    const weekTotal = u2000SumWeekHoursOnTimesheetBankRow(row);
    if (weekTotal === 0) {
      continue;
    }
    if (ABSENCE_BANK_IDS.has(bankId)) {
      absenceHours += weekTotal;
    } else if (bankId === "special") {
      specialWithdraw += weekTotal;
    } else if (bankId === "specialDeposit") {
      specialDeposit += weekTotal;
    }
  }

  return {
    absenceHours,
    specialWithdraw,
    specialDeposit,
    specialNet: specialWithdraw - specialDeposit,
  };
}

/** Totaux croisés onglet Honoraires : absences uniquement, par jour. */
export function u2000SumCrossTabAbsenceHoursFooter(
  list: readonly U2000TimesheetBankAggregateRow[] | undefined | null,
): ReturnType<typeof u2000SumHonorariumDayTotals> {
  const rows = list ?? [];
  const absenceRows = rows.filter((r) => ABSENCE_BANK_IDS.has(String(r.bankId ?? "").trim()));
  return u2000SumHonorariumDayTotals(absenceRows);
}

/** Totaux croisés onglet Honoraires : banque accumulée (retrait − dépôt), par jour. */
export function u2000SumCrossTabSpecialNetHoursFooter(
  list: readonly U2000TimesheetBankAggregateRow[] | undefined | null,
): ReturnType<typeof u2000SumHonorariumDayTotals> {
  const rows = list ?? [];
  const withdrawRows = rows.filter((r) => String(r.bankId ?? "").trim() === "special");
  const depositRows = rows.filter((r) => String(r.bankId ?? "").trim() === "specialDeposit");
  const withdraw = u2000SumHonorariumDayTotals(withdrawRows);
  const deposit = u2000SumHonorariumDayTotals(depositRows);
  const daySums: Record<string, number> = {};
  for (const d of WEEK_DAYS) {
    daySums[d] = (withdraw.daySums[d] ?? 0) - (deposit.daySums[d] ?? 0);
  }
  return { daySums, grandTotal: withdraw.grandTotal - deposit.grandTotal };
}

/** Totaux croisés onglet Honoraires : absences + (retrait − dépôt) par jour (somme des deux pieds séparés). */
export function u2000SumCrossTabBankHoursFooter(
  list: readonly U2000TimesheetBankAggregateRow[] | undefined | null,
): ReturnType<typeof u2000SumHonorariumDayTotals> {
  const absence = u2000SumCrossTabAbsenceHoursFooter(list);
  const specialNet = u2000SumCrossTabSpecialNetHoursFooter(list);
  const daySums: Record<string, number> = {};
  for (const d of WEEK_DAYS) {
    daySums[d] = (absence.daySums[d] ?? 0) + (specialNet.daySums[d] ?? 0);
  }
  return { daySums, grandTotal: absence.grandTotal + specialNet.grandTotal };
}
